import { Close, FileDownload } from '@/components/icons'
import { APP_CUSTOM_ERROR } from '@/constants/MessageConstants'
import { useGetCsvTemplate, verifyMatchesCSV } from '@/services'
import { downloadCsvTemplate } from '@/utils/csvTemplate'
import { Dialog, Transition } from '@headlessui/react'
import Papa from 'papaparse'
import { Fragment, useState } from 'react'
import Dropzone from 'react-dropzone'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { CsvTemplateColumnReference } from './CsvTemplateColumnReference'
import { MatchesTableModal } from './MatchesTableModal'

type Props = {
  open: boolean
  onClose: () => void
  clubId: string
  onRefresh: () => void
}

export const ImportMatchesModal = ({ open, onClose, clubId, onRefresh }: Props) => {
  const { t } = useTranslation()

  const [files, setFile] = useState<any>()
  const [openTable, setOpenTable] = useState(false)
  const [tableData, setTableData] = useState<any[]>([])
  const [errors, setErrors] = useState<any>()
  const [isLoading, setIsLoading] = useState(false)

  // Date format state shared with the table modal
  const [dateFormat, setDateFormat] = useState('yyyy-MM-dd')

  const { data: template, isFetching: templateLoading } = useGetCsvTemplate('single-match-import', open)

  const handleDrop = (acceptedFile: any) => {
    setFile(acceptedFile)
    const file = acceptedFile[0]

    if (file) {
      Papa.parse(file, {
        complete: (result) => {
          const filterData = result?.data?.filter((item: any) => Object.keys(item).length > 0)
          setTableData(filterData as any[])
        },
        header: true,
        delimiter: ',',
        skipEmptyLines: true,
      })
      setOpenTable(true)
    }
  }

  const handleClose = () => {
    onClose()
    setFile([])
  }

  const closeTableModal = () => {
    setOpenTable(false)
    setErrors(null)
  }

  const onReUpload = () => {
    closeTableModal()
    setFile([])
  }

  const onUpload = async () => {
    setIsLoading(true)
    try {
      const res = await verifyMatchesCSV(clubId, files[0], dateFormat)

      // If status is success but response actually contains an error string (raw body)
      if (res.status === 'FAILURE' || typeof res === 'string') {
        setErrors(res)
        setOpenTable(true)
      } else if (res.status === 'SUCCESS') {
        handleClose()
        closeTableModal()
        onRefresh()
        toast.success('Matches Added Successfully')
      }
      setIsLoading(false)
    } catch (error: any) {
      setIsLoading(false)
      // Check if the error object itself contains the raw response string
      const serverError = error?.response?.data || error?.message || APP_CUSTOM_ERROR
      setErrors(serverError)
      setOpenTable(true)
    }
  }

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full flex flex-col gap-y-4 max-w-4xl transform overflow-hidden rounded-2xl bg-white px-6 py-4 text-left align-middle shadow-xl transition-all relative">
                <button
                  className="w-9 h-9 rounded-full bg-navy-900 bg-opacity-10 flex items-center justify-center ml-2 hover:bg-opacity-20 absolute right-2 top-2"
                  onClick={handleClose}
                >
                  <Close className="text-navy-900" />
                </button>
                <Dialog.Title as="h3" className="text-xl text-navy-900 font-semibold">
                  {t('club.importMatches')}
                </Dialog.Title>

                <div className="flex flex-col gap-y-4">
                  <div className="flex flex-row justify-between items-start gap-x-4">
                    <div className="text-sm text-navy-900 text-opacity-70">
                      <p>
                        {template?.result?.description ?? 'Download our CSV template to upload match results in bulk.'}
                      </p>
                    </div>
                    <button
                      className="px-3 py-2 bg-navy-900 bg-opacity-10 hover:bg-opacity-20 hover:transition-all hover:ease-in-out hover:duration-300 text-navy-900 text-sm font-semibold flex gap-x-1 items-center justify-center rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!template?.result || templateLoading}
                      onClick={() => template?.result && downloadCsvTemplate(template.result)}
                    >
                      <FileDownload className="w-5 h-5 text-navy-900" />
                      {t('common.downloadTemplate')}
                    </button>
                  </div>
                  <CsvTemplateColumnReference template={template?.result} isLoading={templateLoading} />

                  {!files?.length ? (
                    <Dropzone onDrop={handleDrop}>
                      {({ getRootProps, getInputProps }) => (
                        <div
                          className="flex items-center justify-center rounded-3xl bg-navy-900 bg-opacity-10 py-12 cursor-pointer"
                          {...getRootProps()}
                        >
                          <input {...getInputProps()} />
                          <span className="text-xs text-navy-900 font-medium">
                            Drag your CSV file here or <span className="text-primary-500">Browse Files</span>
                          </span>
                        </div>
                      )}
                    </Dropzone>
                  ) : (
                    <div>
                      <ul>
                        {files?.map((file: any) => (
                          <li key={file.name} className="text-navy-900 text-opacity-70 text-sm">
                            {file.name}
                          </li>
                        ))}
                      </ul>
                      <button
                        className="text-sm font-semibold text-primary-500 hover:text-opacity-70"
                        onClick={() => {
                          setFile([])
                        }}
                      >
                        {t('common.removeFile')}
                      </button>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
        <MatchesTableModal
          open={openTable}
          onClose={closeTableModal}
          tableData={tableData}
          onReUpload={onReUpload}
          onUpload={onUpload}
          errors={errors}
          loadingUploadCSV={isLoading}
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
        />
      </Dialog>
    </Transition>
  )
}
